import React, { useCallback, useEffect } from 'react';

import { useGlobalSearchParams, useRouter } from 'expo-router';
import { ICartEntry } from 'features/cart/types';
import { useBestPromotion } from 'features/promotions/hooks/use-best-promotion';
import {
  PromoMessageContent,
  PromotionRereadSnapshot,
  shouldClearAfterFailedReread,
} from 'features/promotions/types';
import { useStoreContext } from 'features/store';
import { AppState, LayoutChangeEvent, StyleSheet, Text, View } from 'react-native';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import SanityBlockRenderer from 'components/sanity-block-renderer';
import { toaster } from 'components/toaster/toaster';
import { useAuthRequiredInModal } from 'pages/authentication/components/auth-required';
import { ActivatablePromoRibbon } from 'pages/loyalty/loyalty-promo-message-ribbon/activatable-promo-ribbon';
import { SimplePromoRibbon } from 'pages/loyalty/loyalty-promo-message-ribbon/simple-promo-ribbon';
import { ModalAuthTrigger } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { useUIContext } from 'state/ui';
import { ZIndex, primitive } from 'styles/constants';
import { fontSizes } from 'styles/constants/fonts';
import { logger } from 'utils/logger';

export const PromoMessageRibbon = ({
  cartEntries,
  rounded = false,
  skipStoreDayPartHours = false,
}: {
  cartEntries?: ILegacyCartEntry[] | ICartEntry[];
  rounded?: boolean;
  skipStoreDayPartHours?: boolean;
}) => {
  const { activatePromo } = useGlobalSearchParams<{ activatePromo?: string }>();
  const router = useRouter();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { loyaltyUser } = useLoyaltyUser();
  const loyaltyId = loyaltyUser?.id;
  const { store } = useStoreContext();
  const autoActivate = activatePromo === 'true';

  useAuthRequiredInModal({
    skip: !loyaltyEnabled || !autoActivate,
    trigger: ModalAuthTrigger.Global,
    // Prevent the modal from being displayed again on page refresh after user closes it
    onModalClose: () => router.setParams({ activatePromo: '' }),
  });

  const { promoMessageHeightRef } = useUIContext();

  const clearActivateParam = useCallback(() => {
    // Clear the activatePromo param once the promotion is already activated or
    // doesn't require activation.
    if (activatePromo) {
      router.setParams({ activatePromo: '' });
    }
  }, [activatePromo, router]);

  const {
    promotionId,
    mustActivate,
    userActivatedAt,
    content,
    refetchPromotion,
  } = useBestPromotion({
    cartEntries,
    storeId: store?.number ?? undefined,
    skipStoreDayPartHours,
    onActivateResolved: clearActivateParam,
  });

  // The backend read is authoritative for whether a promotion is currently
  // activatable. A long-lived session can hold a promotion that expires while
  // backgrounded, so refetch on foreground to drop a stale ribbon before it can
  // be tapped.
  //
  // Multiple mounted ribbons each add a listener, but that is left per-instance
  // on purpose: refetchQueries is document-scoped and AppState fires listeners
  // synchronously, so Apollo query deduplication collapses the concurrent
  // refetches into one request per variable-set.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', state => {
      if (state === 'active') {
        // A foreground refetch can reject while offline or when the query
        // fails. Guard it so the failure is observable and never surfaces as an
        // unhandled promise rejection.
        refetchPromotion().catch(error => {
          logger.error({ error, message: 'Failed to refetch best promotion on foreground' });
        });
      }
    });
    return () => subscription.remove();
  }, [refetchPromotion]);

  const handleActivationResolved = useCallback(
    async ({ succeeded }: { succeeded: boolean }) => {
      // Surface feedback before the re-read so a failed refetch can't swallow
      // the error toast or block clearing the deep-link param.
      if (!succeeded) {
        toaster.negative(t('promotionActivationError', 'Something went wrong. Please try again.'));
      } else if (autoActivate) {
        // Clear only on success so a transient failure can retry on next mount.
        router.setParams({ activatePromo: '' });
      }

      // Re-read on success and failure so a server-rejected (expired/invalid)
      // promotion clears from every ribbon sharing it; guarded to stay observable.
      let reread: PromotionRereadSnapshot;
      try {
        reread = await refetchPromotion();
      } catch (error) {
        logger.error({ error, message: 'Failed to refetch best promotion after activation' });
        // Transient failure (offline/query error): keep the deep-link param so a
        // retry can run instead of stranding a promo the reread never confirmed.
        return;
      }

      // Only a failed auto-activation needs cleanup; shouldClearAfterFailedReread
      // decides keep-for-retry vs clear from the authoritative reread.
      if (!succeeded && autoActivate && shouldClearAfterFailedReread(promotionId, reread)) {
        router.setParams({ activatePromo: '' });
      }
    },
    [refetchPromotion, autoActivate, router, promotionId]
  );

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      promoMessageHeightRef.current = event.nativeEvent.layout.height;
    },
    [promoMessageHeightRef]
  );

  const message = mustActivate
    ? renderRibbonMessage({ content, userActivatedAt, mustActivate })
    : renderRibbonMessage({ content });

  if (!message) {
    promoMessageHeightRef.current = 0;
    return null;
  }

  return (
    <View onLayout={handleLayout}>
      {mustActivate ? (
        <ActivatablePromoRibbon
          promotionMeta={{ name: content?.name, loyaltyEngineId: content?.loyaltyEngineId }}
          message={message}
          loyaltyId={loyaltyId!}
          promotionId={promotionId!}
          userActivatedAt={userActivatedAt}
          onActivationResolved={handleActivationResolved}
          autoActivate={autoActivate}
          rounded={rounded}
        />
      ) : (
        <SimplePromoRibbon message={message} rounded={rounded} />
      )}
    </View>
  );
};

function renderRibbonMessage({
  userActivatedAt,
  content,
  mustActivate,
}: {
  userActivatedAt?: string | null;
  content?: PromoMessageContent | null;
  mustActivate?: boolean | null;
}): React.ReactElement | null {
  const promoMessage = content?.promoMessage;
  const blockContent = userActivatedAt
    ? content?.activatedPromoMessageBlock
    : content?.promoMessageBlock;

  if (blockContent) {
    return (
      <SanityBlockRenderer
        content={blockContent}
        style={styles.blockRendererWrapper}
        textProps={{
          color: primitive.$bbqBrown,
          textAlign: mustActivate ? 'left' : 'center',
          fontSize: fontSizes.xs,
          lineHeight: fontSizes.md,
        }}
      />
    );
  }

  if (promoMessage) {
    return (
      <Text style={[styles.ribbonText, mustActivate && styles.activablePromotionText]}>
        {promoMessage}
      </Text>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  blockRendererWrapper: {
    zIndex: ZIndex.MAX,
    flex: 1,
  },
  ribbonText: {
    fontFamily: 'Flame-Regular',
    fontSize: fontSizes.xs,
    color: primitive.$bbqBrown,
    textAlign: 'center',
    zIndex: ZIndex.MAX,
  },
  activablePromotionText: {
    textAlign: 'left',
  },
});
